import { useMemo } from "react";

import { API_BASE_URL } from "@/api/api.config";
import useLazyRestQuery from "@/remoteState/useLazyRestQuery";
import {
  Assignment,
  AssignmentConfig,
} from "@/types/models/Assignment.interface";
import { UserProperties } from "@/types/models/UserProperties.interface";
import stringifyParams from "@/utils/stringifyParams";
import { CONFIG_KEYS } from "@/view/AdditionalAssignmentSettings/AdditionalAssignmentSettings.constants";
import getAssignmentSetting from "@/view/utils/getAssignmentSetting";

interface IProps {
  contentId?: string;
  userProperties?: EligibleUserProperties;
  assignment?: Assignment | undefined;
  skip?: boolean;
}

type EligibleUserProperties = Pick<UserProperties, "isStudent" | "isTeacher">;

/**
 * @private
 * This method is extracted into an exported utility to make it testable.
 */
export const getChecksForUnderstandingResource = ({
  contentId,
  userProperties,
  assignment,
  skip = false,
}: IProps) => {
  const resource = `${API_BASE_URL}/api/v3/checks-for-understanding-quiz-submission/`;
  const { isStudent, isTeacher } = userProperties || {};
  const isChecksForUnderstandingHidden = getAssignmentSetting(
    CONFIG_KEYS.hide_checks_for_understanding as keyof AssignmentConfig,
    assignment,
  );
  // Skip value allows us to ensure we only request a student's submissions related to a specific article_content_id
  // when they have an assignment with checks for understanding enabled for that article.
  const shouldSkip =
    skip ||
    (!isStudent && !isTeacher) ||
    !contentId ||
    isChecksForUnderstandingHidden;
  if (shouldSkip) return undefined;
  const queryString = stringifyParams({
    ...(contentId ? { article_content_id: contentId } : {}),
  });
  return `${resource}${queryString}`;
};

const useLazySubmissionsGet = ({
  contentId,
  userProperties,
  assignment,
  skip = false,
}: IProps) => {
  const resource = useMemo(() => {
    // @ts-expect-error it complains that IProps != IProps. Ignore it.
    return getChecksForUnderstandingResource({
      contentId,
      userProperties,
      assignment,
      skip,
    });
  }, [contentId, userProperties, assignment]);
  // @ts-expect-error when resource is undefined, the API call is skipped.
  return useLazyRestQuery(resource);
};

export default useLazySubmissionsGet;
